'use client';
import React, { FC } from 'react';
import Link from 'next/link';
import '../../styles/components/buttons.css';
import { useFormikContext } from 'formik';
import { LoadingIndicator } from './LoadingIndicator';

interface ButtonProps {
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  className?: string;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const Button: FC<ButtonProps> = ({
  href,
  onClick,
  className,
  children,
  loading = false,
  type = 'button',
  disabled = false,
  ...props
}) => {
  const formik = useFormikContext();

  const isDisabled = loading || formik?.isSubmitting || disabled;

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  return href ? (
    <Link className={className} href={href} {...props}>
      {children}
    </Link>
  ) : (
    <button
      disabled={isDisabled}
      type={type}
      className={className}
      onClick={handleOnClick}
      style={{
        pointerEvents: 'auto',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        ...props.style,
      }}
      aria-disabled={isDisabled}
      {...props}
    >
      {loading || formik?.isSubmitting ? <LoadingIndicator /> : children}
    </button>
  );
};

export default Button;
